// https://local.rests.afisha.ru:80/msk/restaurants/?featureFlags=SberID

import { setUserLoading, setUserData } from '@app/model/slice'
import { globalStore } from '@app/model/store'

import getSberIdFlag from '@shared/lib/helpers/featureFlag'
import { LightGQL } from '@shared/api/gql-service/LightGQLService'
import { GQL_TAGS } from '@shared/api/api.gql'
import { isSberIdSdkLoaded } from '@shared/lib/helpers/externalScripts/sberIdScript'
import { API } from '@shared/api'
import { mergeDeep, isObject } from '../../../src/2_pages/OwnerPersonalPage/helpers'
import parseCookie from '@shared/lib/helpers/parseCookie.js'

// типы для sber авторизации
interface AuthInitData {
  AfishaUid: string
  State: string
  Nonce: string
  CallbackUrl: string
  ClientId: string
}

interface SberUser {
  id: string
  name: string
  email: string
  phone?: string
  image?: {
    url: string
  }
}

interface UserMe {
  afisha_id: number | null
  username: string
  first_name: string
  last_name: string
  photo: string | null
  email: string | null
  phone: string | null
  gender: string | null
  stf: boolean
  spr: boolean
  favorites_objects: {
    restaurants: Record<string, any>
    news: Record<string, any>
    selections: Record<string, any>
    concerts: Record<string, any>
  }
  owner_rests: any[]
  owner_perms: Record<string, any>
}

type AuthState = 'sber_init' | 'sber_login' | 'sber_logout'

declare global {
  interface Window {
    SberidSDK?: any
    SberSDKInstance?: any
  }
}

// константы конфигурации
const SBER_CONFIG = {
  domain: 'id.sber.ru', // isDevMode ? 'id-ift.sber.ru' : 'id.sber.ru'
  protocol: 'https:',
  scope: 'openid name email mobile birthdate gender',
  responseType: 'code',
  appName: 'Afisha Restaurants',
  clientType: 'PRIVATE',
  notificationEnabled: false,
  personalizationEnabled: false,
} as const

const TIMEOUTS = {
  sdkLoad: 5000,
  checkInterval: 100,
} as const

const COOKIE_NAMES = {
  afishaAuthStage: '_afisha_auth_stage',
  afishaAuth: '_afisha_auth',
  afishaIdJWT: '_afishaidJWT',
} as const

const AUTH_STATES = {
  init: 'sber_init',
  login: 'sber_login',
  logout: 'sber_logout',
} as const

// состояние sdk
let sdkInstance: any = null

// утилита, чтобы дождаться загрузки внешнего sdk, который добавляется через addSberIDScript
const waitForSdkLoad = async (timeout = TIMEOUTS.sdkLoad) => {
  const start = Date.now()

  // быстрый выход, если sdk уже есть
  if (isSberIdSdkLoaded() || typeof window.SberidSDK !== 'undefined') return

  return new Promise<void>((resolve, reject) => {
    const check = () => {
      if (isSberIdSdkLoaded() || typeof window.SberidSDK !== 'undefined') {
        resolve()
        return
      }

      if (Date.now() - start > timeout) {
        reject(new Error('sberid: sdk load timeout'))
        return
      }

      // пробуем снова через заданный интервал
      setTimeout(check, TIMEOUTS.checkInterval)
    }

    check()
  })
}

// создание экземпляра sdk после загрузки
const createSdkInstance = async (authInitData?: AuthInitData): Promise<any> => {
  sdkInstance && console.dev('sberid createSdkInstance: sdkInstance', sdkInstance)
  if (sdkInstance) return sdkInstance

  try {
    // дожидаемся, пока внешний скрипт sdk будет загружен
    await waitForSdkLoad()

    // если authInitData не передан, получаем его
    let state = authInitData
    if (!state) {
      state = await getAuthInitData()
    }

    if (!window?.SberidSDK) {
      throw new Error('sberid: window.SberidSDK not found')
    }

    sdkInstance = await new window.SberidSDK({
      baseUrl: `${SBER_CONFIG.protocol}//${SBER_CONFIG.domain}`,
      oidc: {
        client_id: state.ClientId,
        client_type: SBER_CONFIG.clientType,
        nonce: state.Nonce,
        redirect_uri: state.CallbackUrl,
        state: state.State,
        scope: SBER_CONFIG.scope,
        response_type: SBER_CONFIG.responseType,
        name: SBER_CONFIG.appName,
      },
      notification: { enable: SBER_CONFIG.notificationEnabled },
      personalization: SBER_CONFIG.personalizationEnabled,
    }).init()

    console.dev('sberid: sdk instance created', sdkInstance)

    return sdkInstance
  } catch (e) {
    console.error('sberid: createSdkInstance error', e, e.message)
    throw e
  }
}

// получение данных для инициализации авторизации
const getAuthInitData = async (): Promise<AuthInitData> => {
  // const base = !isDevMode && isProdHost ? 'https://www.afisha.ru' : 'https://stage03.afisha.ru'
  const base = 'https://www.afisha.ru'
  const url = `${base}/api/auth/authInitData/`

  const requestUri = typeof window !== 'undefined' ? window.location.href : '/'

  const body = {
    RequestUri: requestUri,
    Source: 'Restaurants',
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': 'true',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  })

  if (!resp.ok) {
    console.error('sberid: authInitData error', resp.status)
    throw new Error('authInitData error')
  }

  return resp.json()
}

const mapViewerUserToUserMe = (user: SberUser): UserMe => {
  const idStr = typeof user?.id === 'string' ? user.id : ''
  const afishaId = Number(idStr.replace(/^User_/, '')) || null
  const fullName = user?.name || ''
  const parts = fullName.trim().split(' ')
  const firstName = parts[0] || ''
  const lastName = parts.slice(1).join(' ') || ''

  return {
    afisha_id: afishaId,
    username: user?.email || fullName || '',
    first_name: firstName,
    last_name: lastName,
    photo: user?.image?.url || null,
    email: user?.email || null,
    phone: user?.phone || null,
    gender: null,
    stf: false,
    spr: false,
    favorites_objects: { restaurants: {}, news: {}, selections: {}, concerts: {} },
    owner_rests: [],
    owner_perms: {},
  }
}

// сравниваем объекты и мерджим, если в первом объекте falsy value
const smartMergeUserData = (target: UserMe, source: UserMe): UserMe => {
  const result = { ...target }

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const targetValue = target[key]
      const sourceValue = source[key]

      if (isObject(targetValue) && isObject(sourceValue)) {
        result[key] = smartMergeUserData(targetValue, sourceValue)
      } else if (!targetValue) {
        result[key] = sourceValue
      }
    }
  }

  return result
}

const BASE_DOMAIN = process.env.IS_STAGE === 'true' ? 'https://artemis.dev.rests.afisha.ru' : 'https://www.afisha.ru'

// удаляем текущую сессию из rest.afisha.ru
const authorizationRestsLogout = async () => {
  console.dev('authorizationRestsLogout')

  try {
    const reqToken = parseCookie('csrftoken')
    console.dev('authorizationRestsLogout reqToken', reqToken)
    // if (!reqToken) return false

    const response = await fetch(`${BASE_DOMAIN}/rests/auth/unregistercurrentsession/`, {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        'X-CSRFToken': reqToken,
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': 'true',
      },
    })

    console.dev('authorizationRestsLogout response', response)

    return response
  } catch (e) {
    console.error('authorizationRestsLogout error', e)
    return false
  }
}

// удаляем куку по ее имени
const removeCookie = (name: string) => {
  const afishaDomain = '.afisha.ru'
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${afishaDomain}; path=/`
  return true
}

const authEvents = async (state) => {
  const isDevMode = !getSberIdFlag()

  globalStore.dispatch(setUserLoading(true))

  console.dev('sberid: authEvents state', state)

  // создаем экземпляр sdk в начале функции
  if (state === 'sber_init') {
    try {
      console.dev('sberid: authEvents createSdkInstance start')

      window.SberSDKInstance = await createSdkInstance()
      console.dev('sberid: authEvents SberSDKInstance', window.SberSDKInstance)

      if (typeof window.SberSDKInstance !== 'undefined') {
        globalStore.dispatch(setUserLoading(false))

        try {
          console.dev(' sberid: viewer start')

          const viewer = await LightGQL.afisha.query(GQL_TAGS.VIEWER, { requestInit: { credentials: 'include' } })
          console.dev('sberid: viewer response', viewer.viewer.user)

          // маппим данные viewer.user в формат стора и диспатчим
          const user = viewer?.viewer?.user
          if (user) {
            const mapped = mapViewerUserToUserMe(user)
            console.dev('sberid: user mapped', mapped)
            globalStore.dispatch(setUserData(mapped))

            // user me
            console.dev('sber_login user_me start')
            const userData = await API.user_me({ repeated: true })
            console.dev('sber_login user_me', JSON.parse(JSON.stringify(userData)))

            if (userData) {
              const mergedUserData = smartMergeUserData(mapped, userData)
              console.dev('sber_login mergedUserData', JSON.parse(JSON.stringify(mergedUserData)))
              globalStore.dispatch(setUserData(mergedUserData))
            } else {
              throw new Error('Не удалось получить данные пользователя', { cause: userData })
            }
          }
        } catch (e) {
          console.error('sber_login viewer error', e)
        }
      }
    } catch (e) {
      console.error('sberid: failed to create sdk instance', e)
      return
    }
  }

  if (state === 'sber_login') {
    console.dev('sber_login start')

    try {
      console.dev('sber_login viewer start')

      const viewer = await LightGQL.afisha.query(GQL_TAGS.VIEWER, { requestInit: { credentials: 'include' } })
      console.dev('sber_login viewer', viewer)

      // маппим данные viewer.user в формат стора и диспатчим
      const user = viewer?.viewer?.user
      if (user) {
        const mapped = mapViewerUserToUserMe(user)
        globalStore.dispatch(setUserData(mapped))
      }
    } catch (e) {
      console.error('sber_login viewer error', e)
    }

    const openAuth = async () => {
      console.dev('sber_login openAuth start')
      try {
        const authInitData = await getAuthInitData()
        console.dev('sber_login authInitData', authInitData)
        // window.SberSDKInstance = await createSdkInstance(authInitData)

        const link = window.SberSDKInstance.getLink()
        console.dev('sberid: redirect to', link)

        window.location.href = link
      } catch (e) {
        console.error('sberid: openAuth error', e)
      }
    }

    openAuth()
  }

  if (state === 'sber_logout') {
    console.dev('sber_logout', state === 'sber_logout')

    try {
      // 1. удаляем куки авторизации

      await removeCookie(COOKIE_NAMES.afishaAuthStage)
      await removeCookie(COOKIE_NAMES.afishaAuth)
      await removeCookie(COOKIE_NAMES.afishaIdJWT)

      // 2. очищаем данные пользователя в сторе
      globalStore.dispatch(setUserData(null))

      const logoutResponse = await authorizationRestsLogout()
      console.dev('sber_logout logoutResponse', logoutResponse)

      if (logoutResponse) {
        window.location.reload()
      }
    } catch (e) {
      console.error('Ошибка при выходе через SberID SDK', e)
    }
  }
}

// экспорт основного объекта с функциями
const SberAuth = {
  init: () => authEvents(AUTH_STATES.init),
  login: () => authEvents(AUTH_STATES.login),
  logout: () => authEvents(AUTH_STATES.logout),
}

export default SberAuth
